import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Plus, KeyRound, AlertTriangle } from "lucide-react";
import Layout from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { API } from "../../App";
import { LEASE_STATUS_LABELS, LEASE_STATUS_COLORS, formatINR, fmtDate } from "./_rentalHelpers";

const emptyLease = {
  rental_unit_id: "", tenant_lead_id: "",
  monthly_rent: "", security_deposit: "", maintenance_amount: "",
  lease_start: "", lease_end: "", lock_in_months: "", notice_period_days: "",
  rent_due_day: "", status: "draft",
  kyc_status: "pending", police_verification_status: "pending",
  renewal_reminder_date: "", agreement_document_url: "", notes: "",
};

export default function RentalLeasesPage() {
  const [searchParams] = useSearchParams();
  const [leases, setLeases] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [renewals, setRenewals] = useState(searchParams.get("renewals") || "");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyLease);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (renewals) params.renewals_within_days = Number(renewals);
      const [l, u, t] = await Promise.all([
        axios.get(`${API}/rentals/leases`, { params }),
        units.length ? Promise.resolve({ data: units }) : axios.get(`${API}/rentals/units`, { params: { limit: 500 } }),
        tenants.length ? Promise.resolve({ data: tenants }) : axios.get(`${API}/rentals/tenant-leads`, { params: { limit: 500 } }),
      ]);
      setLeases(l.data || []);
      setUnits(u.data || []);
      setTenants(t.data || []);
    } catch (e) {
      toast.error("Failed to load leases");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statusFilter, renewals]);

  const startCreate = () => { setEditing(null); setForm(emptyLease); setShowCreate(true); };
  const startEdit = (l) => {
    setEditing(l);
    setForm({
      ...emptyLease, ...l,
      monthly_rent: l.monthly_rent ?? "",
      security_deposit: l.security_deposit ?? "",
      maintenance_amount: l.maintenance_amount ?? "",
      lock_in_months: l.lock_in_months ?? "",
      notice_period_days: l.notice_period_days ?? "",
      rent_due_day: l.rent_due_day ?? "",
      lease_start: l.lease_start ? l.lease_start.slice(0, 10) : "",
      lease_end: l.lease_end ? l.lease_end.slice(0, 10) : "",
      renewal_reminder_date: l.renewal_reminder_date ? l.renewal_reminder_date.slice(0, 10) : "",
    });
    setShowCreate(true);
  };

  const submit = async () => {
    if (!form.monthly_rent || !form.lease_start || !form.lease_end) {
      toast.error("Monthly rent, start and end dates required"); return;
    }
    if (!editing && (!form.rental_unit_id || !form.tenant_lead_id)) {
      toast.error("Unit and tenant required"); return;
    }
    const payload = {
      ...form,
      monthly_rent: Number(form.monthly_rent),
      security_deposit: form.security_deposit === "" ? null : Number(form.security_deposit),
      maintenance_amount: form.maintenance_amount === "" ? null : Number(form.maintenance_amount),
      lock_in_months: form.lock_in_months === "" ? null : Number(form.lock_in_months),
      notice_period_days: form.notice_period_days === "" ? null : Number(form.notice_period_days),
      rent_due_day: form.rent_due_day === "" ? null : Number(form.rent_due_day),
    };
    try {
      if (editing) {
        // Only updatable fields
        const upd = { ...payload };
        delete upd.rental_unit_id; delete upd.tenant_lead_id;
        await axios.put(`${API}/rentals/leases/${editing.lease_id}`, upd);
        toast.success("Lease updated");
      } else {
        await axios.post(`${API}/rentals/leases`, payload);
        toast.success("Lease created");
      }
      setShowCreate(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto" data-testid="rental-leases-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <KeyRound className="w-6 h-6 text-emerald-600" /> Leases
            </h1>
            <p className="text-sm text-slate-500">Active agreements & renewals</p>
          </div>
          <Button onClick={startCreate} data-testid="create-lease-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-1" /> New Lease
          </Button>
        </div>

        <Card className="p-3 mb-3 flex flex-wrap items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]" data-testid="lease-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(LEASE_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={renewals || "none"} onValueChange={v => setRenewals(v === "none" ? "" : v)}>
            <SelectTrigger className="w-[200px]" data-testid="renewal-window-filter"><SelectValue placeholder="Renewals window" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No renewal filter</SelectItem>
              <SelectItem value="30">Renewals next 30d</SelectItem>
              <SelectItem value="60">Renewals next 60d</SelectItem>
              <SelectItem value="90">Renewals next 90d</SelectItem>
            </SelectContent>
          </Select>
          {renewals && (
            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Showing renewals in next {renewals} days
            </Badge>
          )}
        </Card>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Unit / Owner</th>
                  <th className="px-3 py-2 text-left">Tenant</th>
                  <th className="px-3 py-2 text-left">Period</th>
                  <th className="px-3 py-2 text-left">Rent</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Loading…</td></tr>}
                {!loading && leases.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">No leases.</td></tr>}
                {leases.map(l => (
                  <tr key={l.lease_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`lease-row-${l.lease_id}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium">{l.unit?.unit_number || "—"}</p>
                      <p className="text-xs text-slate-500">{l.unit?.project_name || "—"} · Owner: {l.unit?.owner_name || "—"}</p>
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium">{l.tenant?.name || "—"}</p>
                      <p className="text-xs text-slate-500">{l.tenant?.phone}</p>
                    </td>
                    <td className="px-3 py-2 text-xs">{fmtDate(l.lease_start)} → {fmtDate(l.lease_end)}</td>
                    <td className="px-3 py-2 text-xs">{formatINR(l.monthly_rent)}/mo</td>
                    <td className="px-3 py-2">
                      <Badge className={LEASE_STATUS_COLORS[l.status] || "bg-slate-100"}>{LEASE_STATUS_LABELS[l.status] || l.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right"><Button variant="ghost" size="sm" onClick={() => startEdit(l)} data-testid={`edit-lease-${l.lease_id}`}>Edit</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>{editing ? "Edit Lease" : "New Lease"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
              {!editing && (
                <>
                  <div>
                    <Label>Unit *</Label>
                    <Select value={form.rental_unit_id} onValueChange={v => setForm({ ...form, rental_unit_id: v })}>
                      <SelectTrigger data-testid="lease-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                      <SelectContent>
                        {units.map(u => <SelectItem key={u.rental_unit_id} value={u.rental_unit_id}>{u.unit_number} · {u.project_name || ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tenant *</Label>
                    <Select value={form.tenant_lead_id} onValueChange={v => setForm({ ...form, tenant_lead_id: v })}>
                      <SelectTrigger data-testid="lease-tenant-select"><SelectValue placeholder="Select tenant" /></SelectTrigger>
                      <SelectContent>
                        {tenants.map(t => <SelectItem key={t.tenant_lead_id} value={t.tenant_lead_id}>{t.name} · {t.phone}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div><Label>Monthly rent *</Label><Input type="number" data-testid="lease-rent-input" value={form.monthly_rent} onChange={e => setForm({ ...form, monthly_rent: e.target.value })} /></div>
              <div><Label>Security deposit</Label><Input type="number" value={form.security_deposit} onChange={e => setForm({ ...form, security_deposit: e.target.value })} /></div>
              <div><Label>Maintenance / mo</Label><Input type="number" value={form.maintenance_amount} onChange={e => setForm({ ...form, maintenance_amount: e.target.value })} /></div>
              <div><Label>Lease start *</Label><Input type="date" data-testid="lease-start-input" value={form.lease_start} onChange={e => setForm({ ...form, lease_start: e.target.value })} /></div>
              <div><Label>Lease end *</Label><Input type="date" data-testid="lease-end-input" value={form.lease_end} onChange={e => setForm({ ...form, lease_end: e.target.value })} /></div>
              <div><Label>Lock-in (months)</Label><Input type="number" value={form.lock_in_months} onChange={e => setForm({ ...form, lock_in_months: e.target.value })} /></div>
              <div><Label>Notice period (days)</Label><Input type="number" value={form.notice_period_days} onChange={e => setForm({ ...form, notice_period_days: e.target.value })} /></div>
              <div><Label>Rent due day</Label><Input type="number" min={1} max={31} value={form.rent_due_day} onChange={e => setForm({ ...form, rent_due_day: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="lease-status-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(LEASE_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>KYC status</Label>
                <Select value={form.kyc_status || "pending"} onValueChange={v => setForm({ ...form, kyc_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem><SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Police verification</Label>
                <Select value={form.police_verification_status || "pending"} onValueChange={v => setForm({ ...form, police_verification_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                    <SelectItem value="not_required">Not required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Renewal reminder date</Label><Input type="date" value={form.renewal_reminder_date} onChange={e => setForm({ ...form, renewal_reminder_date: e.target.value })} /></div>
              <div className="col-span-2"><Label>Agreement document URL</Label><Input value={form.agreement_document_url} onChange={e => setForm({ ...form, agreement_document_url: e.target.value })} placeholder="Paste signed PDF link" /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="submit-lease" className="bg-emerald-600 hover:bg-emerald-700">{editing ? "Save" : "Create lease"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
